/**
 * Time measurement for a session.
 *
 * Duration is **derived from segments**, never accumulated by a timer. That is the whole design,
 * and it exists because every naive approach fails in a way that corrupts an exam result:
 *
 *  - `setInterval` is throttled to once a minute in a background tab, so a counter driven by it
 *    simply loses time;
 *  - `Date.now()` deltas break when the system clock changes, and a laptop that syncs NTP or
 *    crosses a DST boundary mid-exam does exactly that;
 *  - neither survives the tab being suspended.
 *
 * So the session records when it entered and left each state, and the duration is the sum. A timer
 * still runs, but only to redraw the display and to notice expiry — nothing depends on it having
 * fired.
 *
 * The clock reads `performance.now()`, which is monotonic. Wall-clock timestamps are recorded
 * separately, for the exported session, and are never used to compute a duration.
 */

export interface TimeSegment {
  readonly startedAt: number;
  /** Null while the segment is still open. */
  readonly endedAt: number | null;
}

export interface SessionClock {
  readonly segments: readonly TimeSegment[];
  readonly timeLimitMs: number | null;
  readonly expired: boolean;
  /** Times the wall clock and the monotonic clock disagreed by more than a moment. */
  readonly clockAnomalies: number;
  readonly visibilityLostCount: number;
}

export function createClock(timeLimitMs: number | null, at: number): SessionClock {
  return {
    segments: [{ startedAt: at, endedAt: null }],
    timeLimitMs,
    expired: false,
    clockAnomalies: 0,
    visibilityLostCount: 0,
  };
}

export function openSegment(segments: readonly TimeSegment[], at: number): readonly TimeSegment[] {
  const last = segments[segments.length - 1];
  if (last?.endedAt === null) {
    return segments;
  }
  return [...segments, { startedAt: at, endedAt: null }];
}

export function closeSegment(segments: readonly TimeSegment[], at: number): readonly TimeSegment[] {
  const last = segments[segments.length - 1];
  if (last?.endedAt !== null) {
    return segments;
  }
  // Guard against a monotonic clock that appears to go backwards across a worker or tab handoff:
  // a negative segment would subtract time the candidate actually spent.
  const endedAt = Math.max(at, last.startedAt);
  return [...segments.slice(0, -1), { startedAt: last.startedAt, endedAt }];
}

/**
 * The total of every segment, counting an open one up to `at`.
 *
 * `at` is a parameter rather than a call to `performance.now()` so that this stays a pure function
 * — which is what makes the whole engine testable without faking timers.
 */
export function elapsedMs(segments: readonly TimeSegment[], at: number): number {
  let total = 0;
  for (const segment of segments) {
    const end = segment.endedAt ?? Math.max(at, segment.startedAt);
    total += end - segment.startedAt;
  }
  return Math.round(total);
}

export function remainingMs(clock: SessionClock, at: number): number | null {
  if (clock.timeLimitMs === null) {
    return null;
  }
  return Math.max(0, clock.timeLimitMs - elapsedMs(clock.segments, at));
}

export function hasExpired(clock: SessionClock, at: number): boolean {
  const remaining = remainingMs(clock, at);
  return remaining !== null && remaining <= 0;
}
